import React, { useEffect, useState, createContext, useContext } from 'react';
export type Language = 'es' | 'ca' | 'eu' | 'gl' | 'en';
type LanguageContextType = {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
};
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
const translations: Record<Language, Record<string, string>> = {
  es: {
    // Navigation
    'nav.home': 'Inicio',
    'nav.construction': 'Construcción',
    'nav.renovations': 'Reformas',
    'nav.call': 'Llamar ahora',
    // Values
    'values.trust': 'Confianza',
    'values.efficiency': 'Eficiencia',
    'values.strategy': 'Estrategia',
    'values.trust.desc': 'Más de una década construyendo relaciones sólidas con nuestros clientes',
    'values.efficiency.desc': 'Optimizamos cada proceso para cumplir plazos sin comprometer la calidad',
    'values.strategy.desc': 'Planificación meticulosa desde el primer día hasta la entrega final',
    // Hero
    'hero.badge': 'Servicios en toda la provincia de Barcelona',
    'hero.title1': 'Construimos',
    'hero.title2': 'tu futuro hogar',
    'hero.subtitle': 'Especialistas en construcción de casas unifamiliares y reformas integrales. Más de una década de experiencia transformando espacios con profesionalidad, calidad y compromiso absoluto con cada proyecto.',
    'hero.cta.construction': 'Construcción de Casas',
    'hero.cta.renovations': 'Reformas Integrales',
    'hero.trust': 'Tu proyecto en las mejores manos',
    // Services Overview
    'services.label': 'Nuestros Servicios',
    'services.title1': 'Dos especialidades,',
    'services.title2': 'un mismo compromiso',
    'services.subtitle': 'En Habitalum nos especializamos en lo que mejor sabemos hacer: construir hogares y transformar espacios con la máxima calidad. Cada proyecto es único y merece una atención personalizada.',
    'services.construction.title': 'Construcción de Casas Unifamiliares',
    'services.construction.desc': 'Diseñamos y construimos tu hogar desde cero. Casas modernas, rústicas, mediterráneas o tradicionales. Cada proyecto es único y lo tratamos como tal, con atención al detalle y materiales de primera calidad.',
    'services.construction.f1': 'Diseño personalizado',
    'services.construction.f2': 'Materiales premium',
    'services.construction.f3': 'Garantía de calidad',
    'services.construction.f4': 'Plazos garantizados',
    'services.renovations.title': 'Reformas Integrales',
    'services.renovations.desc': 'Transformamos espacios con un equipo altamente especializado. Reformas integrales de viviendas y grandes espacios comerciales. Renovamos cada rincón con la máxima profesionalidad.',
    'services.renovations.f1': 'Equipo especializado',
    'services.renovations.f2': 'Expertos en materiales',
    'services.renovations.f3': 'Precios competitivos',
    'services.renovations.f4': 'Proyectos llave en mano',
    'services.more': 'Ver más información',
    'services.experience': 'Años de experiencia avalan nuestro trabajo',
    // Construction Types
    'construction.type.modern': 'Casa Moderna',
    'construction.desc.modern': 'Líneas limpias, grandes ventanales y espacios abiertos que maximizan la luz natural y la conexión con el exterior.',
    'construction.type.rustic': 'Casa Rústica',
    'construction.desc.rustic': 'El encanto de lo tradicional con el confort moderno. Piedra, madera y calidez en cada detalle.',
    'construction.type.mediterranean': 'Estilo Mediterráneo',
    'construction.desc.mediterranean': 'Blancos luminosos, arcos y conexión con el exterior. La esencia de nuestra tierra en tu hogar.',
    'construction.type.minimalist': 'Diseño Minimalista',
    'construction.desc.minimalist': 'La elegancia de la simplicidad. Espacios depurados, funcionales y atemporales.',
    'construction.type.traditional': 'Obra Tradicional',
    'construction.desc.traditional': 'Construcción sólida y duradera con métodos probados y materiales nobles de la más alta calidad.',
    'construction.type.contemporary': 'Casa Contemporánea',
    'construction.desc.contemporary': 'Diseño actual que se adapta a las tendencias y necesidades de hoy sin perder funcionalidad.',
    'construction.type.country': 'Casa de Campo',
    'construction.desc.country': 'Integrada en la naturaleza, robusta y acogedora para disfrutar de la vida rural.',
    'construction.type.farmhouse': 'Masía Renovada',
    'construction.desc.farmhouse': 'Recuperamos la historia de antiguas masías con técnicas de restauración respetuosas.',
    // Construction Section
    'construction.label': 'Construcción',
    'construction.title': 'Casas Unifamiliares',
    'construction.subtitle': 'Construimos tu hogar desde los cimientos hasta el último detalle. Variedad de estilos adaptados a tus sueños y necesidades. Cada casa que construimos es un proyecto único que tratamos con la máxima dedicación.',
    'construction.form.title': 'Solicita tu presupuesto personalizado',
    'construction.form.subtitle': 'Cuéntanos sobre tu proyecto y te contactaremos en menos de 24 horas con una propuesta detallada y sin compromiso.',
    'construction.why': '¿Por qué elegirnos?',
    'construction.why.desc': 'Con más de 1100 proyectos completados, nuestra experiencia habla por sí sola. Cada casa que construimos refleja nuestro compromiso con la excelencia.',
    // Renovation Types
    'renovation.type.integral': 'Reforma Integral de Vivienda',
    'renovation.desc.integral': 'Transformación total de tu hogar. Renovación completa de instalaciones, distribución y acabados de primera calidad.',
    'renovation.type.commercial': 'Local Comercial',
    'renovation.desc.commercial': 'Reformas integrales para negocios. Diseño estratégico para potenciar tu marca y atraer clientes.',
    'renovation.type.office': 'Oficinas Corporativas',
    'renovation.desc.office': 'Espacios de trabajo modernos y eficientes. Renovación completa de oficinas que inspiran productividad.',
    'renovation.type.business': 'Negocios y Hostelería',
    'renovation.desc.business': 'Reformas de gran envergadura para restaurantes, hoteles y grandes superficies comerciales.',
    // Renovation Section
    'renovation.label': 'Reformas',
    'renovation.title': 'Reformas Integrales',
    'renovation.subtitle': 'Especialistas en grandes proyectos. Reformas integrales de viviendas y espacios comerciales con la máxima exigencia y profesionalidad.',
    'renovation.h1.title': 'Equipo Especializado',
    'renovation.h1.desc': 'Plantilla amplia de profesionales cualificados en todas las disciplinas de la construcción.',
    'renovation.h2.title': 'Expertos en Materiales',
    'renovation.h2.desc': 'Conocimiento profundo de los mejores materiales del mercado para cada tipo de proyecto.',
    'renovation.h3.title': 'Eficiencia Garantizada',
    'renovation.h3.desc': 'Cumplimos plazos sin comprometer la calidad. Planificación rigurosa de cada fase.',
    'renovation.h4.title': 'Precios Competitivos',
    'renovation.h4.desc': 'Presupuestos transparentes y ajustados. La mejor relación calidad-precio del mercado.',
    'renovation.form.title': 'Solicita tu presupuesto de reforma',
    'renovation.form.subtitle': 'Describe tu proyecto y recibirás una propuesta detallada adaptada a tus necesidades específicas.',
    // Forms
    'form.name': 'Nombre completo',
    'form.name.placeholder': 'Tu nombre',
    'form.email': 'Email',
    'form.email.placeholder': 'tu@email.com',
    'form.phone': 'Teléfono',
    'form.phone.placeholder': '+34 600 000 000',
    'form.location': 'Ubicación del proyecto',
    'form.location.placeholder': 'Municipio en Barcelona',
    'form.property.location': 'Ubicación de la propiedad',
    'form.meters': 'Metros cuadrados',
    'form.meters.placeholder': '150 m²',
    'form.project.type': 'Tipo de proyecto',
    'form.project.select': 'Seleccionar...',
    'form.timeline': 'Plazo deseado',
    'form.timeline.asap': 'Lo antes posible',
    'form.timeline.3-6': '3-6 meses',
    'form.timeline.6-12': '6-12 meses',
    'form.timeline.12+': 'Más de 12 meses',
    'form.renovation.type': 'Tipo de reforma',
    'form.budget': 'Presupuesto aproximado',
    'form.budget.less10k': 'Menos de 10.000€',
    'form.budget.10k-25k': '10.000€ - 25.000€',
    'form.budget.25k-50k': '25.000€ - 50.000€',
    'form.budget.50k-100k': '50.000€ - 100.000€',
    'form.budget.100k+': 'Más de 100.000€',
    'form.space.desc': 'Descripción del espacio',
    'form.space.placeholder': 'Ej: Piso de 80m², Local de 200m²...',
    'form.details': 'Detalles adicionales',
    'form.details.placeholder': 'Cuéntanos más sobre tu proyecto ideal...',
    'form.needs': 'Necesidades específicas',
    'form.needs.placeholder': 'Describe qué cambios necesitas...',
    'form.submit': 'Solicitar presupuesto',
    'form.sending': 'Enviando...',
    'form.success.title': '¡Solicitud enviada!',
    'form.success.message': 'Nos pondremos en contacto contigo en menos de 24 horas.',
    'form.required': '*',
    'form.privacy': 'Al enviar este formulario, aceptas nuestra política de privacidad.',
    // Reviews
    'reviews.label': 'Testimonios',
    'reviews.title1': 'Lo que dicen',
    'reviews.title2': 'nuestros clientes',
    'reviews.subtitle': 'La satisfacción de nuestros clientes es nuestra mejor carta de presentación. Más de 1100 proyectos completados avalan nuestra trayectoria.',
    'reviews.tag.construction': 'Construcción',
    'reviews.tag.renovation': 'Reforma',
    'reviews.stat1': 'Proyectos completados',
    'reviews.stat2': 'Años de experiencia',
    'reviews.stat3': 'Clientes satisfechos',
    'reviews.stat4': 'Profesionales',
    // Contact/Footer
    'contact.construction': 'Construcción',
    'contact.construction.desc': 'Proyectos de casas unifamiliares, diseño y construcción desde cero con los mejores materiales.',
    'contact.renovations': 'Reformas',
    'contact.renovations.desc': 'Reformas integrales completas para viviendas y espacios comerciales.',
    'contact.call.question': '¿Prefieres hablar directamente?',
    'contact.cta': 'Solicitar presupuesto',
    'contact.rights': 'Todos los derechos reservados.',
    'contact.privacy': 'Política de privacidad',
    'contact.legal': 'Aviso legal',
    'contact.brand.desc': 'Especialistas en construcción de casas unifamiliares y reformas integrales. Confianza, eficiencia y estrategia en cada proyecto que realizamos.',
    'contact.barcelona': 'Servicios en toda la provincia de Barcelona',
    'contact.tagline': 'Tu hogar, nuestra pasión',
    // Pages
    'page.construction.hero.title1': 'Construcción de',
    'page.construction.hero.title2': 'Casas Unifamiliares',
    'page.construction.hero.subtitle': 'Hacemos realidad el hogar de tus sueños. Desde el diseño hasta la entrega de llaves, te acompañamos en cada paso del proceso.',
    'page.construction.process.label': 'Nuestro Proceso',
    'page.construction.process.title': 'De la idea a la realidad',
    'page.construction.process.step1.title': 'Consulta Inicial',
    'page.construction.process.step1.desc': 'Escuchamos tus ideas, necesidades y presupuesto para entender tu visión.',
    'page.construction.process.step2.title': 'Diseño y Proyecto',
    'page.construction.process.step2.desc': 'Desarrollamos el diseño arquitectónico adaptado a tus necesidades.',
    'page.construction.process.step3.title': 'Permisos y Licencias',
    'page.construction.process.step3.desc': 'Gestionamos toda la documentación necesaria ante las autoridades.',
    'page.construction.process.step4.title': 'Construcción',
    'page.construction.process.step4.desc': 'Ejecutamos la obra con los mejores materiales y profesionales.',
    'page.construction.process.step5.title': 'Entrega',
    'page.construction.process.step5.desc': 'Te entregamos las llaves de tu nuevo hogar listo para vivir.',
    'page.renovation.hero.title1': 'Reformas',
    'page.renovation.hero.title2': 'Integrales',
    'page.renovation.hero.subtitle': 'Transformamos tu espacio por completo. Reformas integrales que renuevan cada rincón con la máxima calidad.',
    'page.renovation.process.label': 'Nuestro Proceso',
    'page.renovation.process.title': 'Reforma sin complicaciones',
    'page.renovation.process.step1.title': 'Visita y Evaluación',
    'page.renovation.process.step1.desc': 'Visitamos tu propiedad para evaluar el estado actual y posibilidades.',
    'page.renovation.process.step2.title': 'Propuesta y Presupuesto',
    'page.renovation.process.step2.desc': 'Elaboramos una propuesta detallada con presupuesto cerrado.',
    'page.renovation.process.step3.title': 'Planificación',
    'page.renovation.process.step3.desc': 'Organizamos el calendario de trabajo para minimizar molestias.',
    'page.renovation.process.step4.title': 'Ejecución',
    'page.renovation.process.step4.desc': 'Realizamos la reforma con supervisión constante de calidad.',
    'page.renovation.process.step5.title': 'Entrega Final',
    'page.renovation.process.step5.desc': 'Revisión final conjunta y entrega del espacio renovado.',
    'page.renovation.why.label': 'Por qué elegirnos',
    'page.renovation.why.title': 'Reformas integrales de confianza',
    'page.renovation.why.item1': 'Presupuesto cerrado sin sorpresas',
    'page.renovation.why.item2': 'Coordinación de todos los gremios',
    'page.renovation.why.item3': 'Materiales de primera calidad',
    'page.renovation.why.item4': 'Limpieza diaria de la obra',
    'page.renovation.why.item5': 'Garantía de 2 años en acabados',
    'page.renovation.why.item6': 'Comunicación constante'
  },
  ca: {
    'nav.home': 'Inici',
    'nav.construction': 'Construcció',
    'nav.renovations': 'Reformes',
    'nav.call': 'Trucar ara',
    'values.trust': 'Confiança',
    'values.efficiency': 'Eficiència',
    'values.strategy': 'Estratègia',
    'values.trust.desc': "Més d'una dècada construint relacions sòlides amb els nostres clients",
    'values.efficiency.desc': 'Optimitzem cada procés per complir terminis sense comprometre la qualitat',
    'values.strategy.desc': 'Planificació meticulosa des del primer dia fins al lliurament final',
    'hero.badge': 'Serveis a tota la província de Barcelona',
    'hero.title1': 'Construïm',
    'hero.title2': 'la teva futura llar',
    'hero.subtitle': "Especialistes en construcció de cases unifamiliars i reformes integrals. Més d'una dècada d'experiència transformant espais amb professionalitat, qualitat i compromís absolut.",
    'hero.cta.construction': 'Construcció de Cases',
    'hero.cta.renovations': 'Reformes Integrals',
    'hero.trust': 'El teu projecte en les millors mans',
    'services.label': 'Els Nostres Serveis',
    'services.title1': 'Dues especialitats,',
    'services.title2': 'un mateix compromís',
    'services.subtitle': 'A Habitalum ens especialitzem en el que millor sabem fer: construir llars i transformar espais amb la màxima qualitat.',
    'services.construction.title': 'Construcció de Cases Unifamiliars',
    'services.construction.desc': 'Dissenyem i construïm la teva llar des de zero. Cases modernes, rústiques, mediterrànies o tradicionals.',
    'services.construction.f1': 'Disseny personalitzat',
    'services.construction.f2': 'Materials premium',
    'services.construction.f3': 'Garantia de qualitat',
    'services.construction.f4': 'Terminis garantits',
    'services.renovations.title': 'Reformes Integrals',
    'services.renovations.desc': "Transformem espais amb un equip altament especialitzat. Reformes integrals d'habitatges i grans espais comercials.",
    'services.renovations.f1': 'Equip especialitzat',
    'services.renovations.f2': 'Experts en materials',
    'services.renovations.f3': 'Preus competitius',
    'services.renovations.f4': 'Projectes clau en mà',
    'services.more': 'Veure més informació',
    'services.experience': "Anys d'experiència avalen el nostre treball",
    'construction.type.modern': 'Casa Moderna',
    'construction.desc.modern': 'Línies netes, grans finestrals i espais oberts que maximitzen la llum natural.',
    'construction.type.rustic': 'Casa Rústica',
    'construction.desc.rustic': "L'encant del tradicional amb el confort modern. Pedra, fusta i calidesa.",
    'construction.type.mediterranean': 'Estil Mediterrani',
    'construction.desc.mediterranean': "Blancs lluminosos, arcs i connexió amb l'exterior. L'essència de la nostra terra.",
    'construction.type.minimalist': 'Disseny Minimalista',
    'construction.desc.minimalist': "L'elegància de la simplicitat. Espais depurats i funcionals.",
    'construction.type.traditional': 'Obra Tradicional',
    'construction.desc.traditional': 'Construcció sòlida i duradora amb mètodes provats i materials nobles.',
    'construction.type.contemporary': 'Casa Contemporània',
    'construction.desc.contemporary': "Disseny actual que s'adapta a les tendències i necessitats d'avui.",
    'construction.type.country': 'Casa de Camp',
    'construction.desc.country': 'Integrada a la natura, robusta i acollidora per a la vida rural.',
    'construction.type.farmhouse': 'Masia Renovada',
    'construction.desc.farmhouse': "Recuperem la història d'antigues masies amb tècniques de restauració respectuoses.",
    'construction.label': 'Construcció',
    'construction.title': 'Cases Unifamiliars',
    'construction.subtitle': "Construïm la teva llar des dels fonaments fins a l'últim detall.",
    'construction.form.title': 'Sol·licita el teu pressupost personalitzat',
    'construction.form.subtitle': "Explica'ns el teu projecte i et contactarem en menys de 24 hores.",
    'construction.why': 'Per què triar-nos?',
    'construction.why.desc': 'Amb més de 1100 projectes completats, la nostra experiència parla per si sola.',
    'renovation.type.integral': "Reforma Integral d'Habitatge",
    'renovation.desc.integral': "Transformació total de la teva llar. Renovació completa d'instal·lacions i acabats.",
    'renovation.type.commercial': 'Local Comercial',
    'renovation.desc.commercial': 'Reformes integrals per a negocis. Disseny estratègic per potenciar la teva marca.',
    'renovation.type.office': 'Oficines Corporatives',
    'renovation.desc.office': "Espais de treball moderns i eficients. Renovació completa d'oficines.",
    'renovation.type.business': 'Negocis i Hostaleria',
    'renovation.desc.business': 'Reformes de gran envergadura per a restaurants, hotels i grans superfícies.',
    'renovation.label': 'Reformes',
    'renovation.title': 'Reformes Integrals',
    'renovation.subtitle': 'Especialistes en grans projectes. Reformes integrals amb la màxima exigència.',
    'renovation.h1.title': 'Equip Especialitzat',
    'renovation.h1.desc': 'Plantilla àmplia de professionals qualificats en totes les disciplines.',
    'renovation.h2.title': 'Experts en Materials',
    'renovation.h2.desc': 'Coneixement profund dels millors materials del mercat.',
    'renovation.h3.title': 'Eficiència Garantida',
    'renovation.h3.desc': 'Complim terminis sense comprometre la qualitat.',
    'renovation.h4.title': 'Preus Competitius',
    'renovation.h4.desc': 'Pressupostos transparents i ajustats. La millor relació qualitat-preu.',
    'renovation.form.title': 'Sol·licita el teu pressupost de reforma',
    'renovation.form.subtitle': 'Descriu el teu projecte i rebràs una proposta detallada.',
    'form.name': 'Nom complet',
    'form.name.placeholder': 'El teu nom',
    'form.email': 'Email',
    'form.email.placeholder': 'tu@email.com',
    'form.phone': 'Telèfon',
    'form.phone.placeholder': '+34 600 000 000',
    'form.location': 'Ubicació del projecte',
    'form.location.placeholder': 'Municipi a Barcelona',
    'form.property.location': 'Ubicació de la propietat',
    'form.meters': 'Metres quadrats',
    'form.meters.placeholder': '150 m²',
    'form.project.type': 'Tipus de projecte',
    'form.project.select': 'Seleccionar...',
    'form.timeline': 'Termini desitjat',
    'form.timeline.asap': 'El més aviat possible',
    'form.timeline.3-6': '3-6 mesos',
    'form.timeline.6-12': '6-12 mesos',
    'form.timeline.12+': 'Més de 12 mesos',
    'form.renovation.type': 'Tipus de reforma',
    'form.budget': 'Pressupost aproximat',
    'form.budget.less10k': 'Menys de 10.000€',
    'form.budget.10k-25k': '10.000€ - 25.000€',
    'form.budget.25k-50k': '25.000€ - 50.000€',
    'form.budget.50k-100k': '50.000€ - 100.000€',
    'form.budget.100k+': 'Més de 100.000€',
    'form.space.desc': "Descripció de l'espai",
    'form.space.placeholder': 'Ex: Pis de 80m², Local de 200m²...',
    'form.details': 'Detalls addicionals',
    'form.details.placeholder': "Explica'ns més sobre el teu projecte ideal...",
    'form.needs': 'Necessitats específiques',
    'form.needs.placeholder': 'Descriu quins canvis necessites...',
    'form.submit': 'Sol·licitar pressupost',
    'form.sending': 'Enviant...',
    'form.success.title': 'Sol·licitud enviada!',
    'form.success.message': 'Ens posarem en contacte amb tu en menys de 24 hores.',
    'form.required': '*',
    'form.privacy': 'En enviar aquest formulari, acceptes la nostra política de privacitat.',
    'reviews.label': 'Testimonis',
    'reviews.title1': 'El que diuen',
    'reviews.title2': 'els nostres clients',
    'reviews.subtitle': 'La satisfacció dels nostres clients és la nostra millor carta de presentació. Més de 1100 projectes completats avalen la nostra trajectòria.',
    'reviews.tag.construction': 'Construcció',
    'reviews.tag.renovation': 'Reforma',
    'reviews.stat1': 'Projectes completats',
    'reviews.stat2': "Anys d'experiència",
    'reviews.stat3': 'Clients satisfets',
    'reviews.stat4': 'Professionals',
    'contact.construction': 'Construcció',
    'contact.construction.desc': 'Projectes de cases unifamiliars, disseny i construcció des de zero.',
    'contact.renovations': 'Reformes',
    'contact.renovations.desc': 'Reformes integrals completes per a habitatges i espais comercials.',
    'contact.call.question': 'Prefereixes parlar directament?',
    'contact.cta': 'Sol·licitar pressupost',
    'contact.rights': 'Tots els drets reservats.',
    'contact.privacy': 'Política de privacitat',
    'contact.legal': 'Avís legal',
    'contact.brand.desc': 'Especialistes en construcció de cases unifamiliars i reformes integrals. Confiança, eficiència i estratègia en cada projecte.',
    'contact.barcelona': 'Serveis a tota la província de Barcelona',
    'contact.tagline': 'La teva llar, la nostra passió',
    'page.construction.hero.title1': 'Construcció de',
    'page.construction.hero.title2': 'Cases Unifamiliars',
    'page.construction.hero.subtitle': 'Fem realitat la llar dels teus somnis. Des del disseny fins al lliurament de claus.',
    'page.construction.process.label': 'El Nostre Procés',
    'page.construction.process.title': 'De la idea a la realitat',
    'page.construction.process.step1.title': 'Consulta Inicial',
    'page.construction.process.step1.desc': 'Escoltem les teves idees, necessitats i pressupost.',
    'page.construction.process.step2.title': 'Disseny i Projecte',
    'page.construction.process.step2.desc': 'Desenvolupem el disseny arquitectònic adaptat a les teves necessitats.',
    'page.construction.process.step3.title': 'Permisos i Llicències',
    'page.construction.process.step3.desc': 'Gestionem tota la documentació necessària.',
    'page.construction.process.step4.title': 'Construcció',
    'page.construction.process.step4.desc': "Executem l'obra amb els millors materials i professionals.",
    'page.construction.process.step5.title': 'Lliurament',
    'page.construction.process.step5.desc': 'Et lliurem les claus de la teva nova llar.',
    'page.renovation.hero.title1': 'Reformes',
    'page.renovation.hero.title2': 'Integrals',
    'page.renovation.hero.subtitle': 'Transformem el teu espai per complet. Reformes integrals que renoven cada racó.',
    'page.renovation.process.label': 'El Nostre Procés',
    'page.renovation.process.title': 'Reforma sense complicacions',
    'page.renovation.process.step1.title': 'Visita i Avaluació',
    'page.renovation.process.step1.desc': "Visitem la teva propietat per avaluar l'estat actual.",
    'page.renovation.process.step2.title': 'Proposta i Pressupost',
    'page.renovation.process.step2.desc': 'Elaborem una proposta detallada amb pressupost tancat.',
    'page.renovation.process.step3.title': 'Planificació',
    'page.renovation.process.step3.desc': 'Organitzem el calendari de treball.',
    'page.renovation.process.step4.title': 'Execució',
    'page.renovation.process.step4.desc': 'Realitzem la reforma amb supervisió constant.',
    'page.renovation.process.step5.title': 'Lliurament Final',
    'page.renovation.process.step5.desc': 'Revisió final conjunta i lliurament.',
    'page.renovation.why.label': 'Per què triar-nos',
    'page.renovation.why.title': 'Reformes integrals de confiança',
    'page.renovation.why.item1': 'Pressupost tancat sense sorpreses',
    'page.renovation.why.item2': 'Coordinació de tots els gremis',
    'page.renovation.why.item3': 'Materials de primera qualitat',
    'page.renovation.why.item4': "Neteja diària de l'obra",
    'page.renovation.why.item5': 'Garantia de 2 anys en acabats',
    'page.renovation.why.item6': 'Comunicació constant'
  },
  eu: {
    'nav.home': 'Hasiera',
    'nav.construction': 'Eraikuntza',
    'nav.renovations': 'Erreformak',
    'nav.call': 'Deitu orain',
    'values.trust': 'Konfiantza',
    'values.efficiency': 'Eraginkortasuna',
    'values.strategy': 'Estrategia',
    'values.trust.desc': 'Hamarkada bat baino gehiago gure bezeroekin harreman sendoak eraikitzen',
    'values.efficiency.desc': 'Prozesu guztiak optimizatzen ditugu epeak betetzeko kalitatea arriskuan jarri gabe',
    'values.strategy.desc': 'Planifikazio zehatza lehen egunetik azken entregaraino',
    'hero.badge': 'Zerbitzuak Bartzelonako probintzia osoan',
    'hero.title1': 'Eraikitzen dugu',
    'hero.title2': 'zure etorkizuneko etxea',
    'hero.subtitle': 'Familia bakarreko etxeen eraikuntzan eta erreforma integraletan espezialistak. Hamarkada bat baino gehiagoko esperientzia espazioak profesionaltasunez eraldatzen.',
    'hero.cta.construction': 'Etxeen Eraikuntza',
    'hero.cta.renovations': 'Erreforma Integralak',
    'hero.trust': 'Zure proiektua esku onenetan',
    'services.label': 'Gure Zerbitzuak',
    'services.title1': 'Bi espezialitate,',
    'services.title2': 'konpromiso bera',
    'services.subtitle': 'Habitalum-en, ondoen egiten dakigunean espezializatzen gara: etxeak eraiki eta espazioak kalitate gorenarekin eraldatu.',
    'services.construction.title': 'Familia Bakarreko Etxeen Eraikuntza',
    'services.construction.desc': 'Zure etxea diseinatzen eta eraikitzen dugu hutsetik. Etxe modernoak, rustikoak, mediterraneoak edo tradizionalak.',
    'services.construction.f1': 'Diseinu pertsonalizatua',
    'services.construction.f2': 'Material premiumak',
    'services.construction.f3': 'Kalitate bermea',
    'services.construction.f4': 'Epe bermatuak',
    'services.renovations.title': 'Erreforma Integralak',
    'services.renovations.desc': 'Espazioak eraldatzen ditugu talde oso espezializatuarekin.',
    'services.renovations.f1': 'Talde espezializatua',
    'services.renovations.f2': 'Materialetan adituak',
    'services.renovations.f3': 'Prezio lehiakorrak',
    'services.renovations.f4': 'Giltza eskuan proiektuak',
    'services.more': 'Informazio gehiago',
    'services.experience': 'Urte askotako esperientziak gure lana bermatzen du',
    'construction.type.modern': 'Etxe Modernoa',
    'construction.desc.modern': 'Lerro garbiak, leiho handiak eta espazio irekiak argi naturala maximizatzen dutenak.',
    'construction.type.rustic': 'Etxe Rustikoa',
    'construction.desc.rustic': 'Tradizionalaren xarma erosotasun modernoarekin. Harria, egurra eta berotasuna.',
    'construction.type.mediterranean': 'Mediterraneo Estiloa',
    'construction.desc.mediterranean': 'Zuri argitsuak, arkuak eta kanpoaldearekin lotura. Gure lurraren esentzia.',
    'construction.type.minimalist': 'Diseinu Minimalista',
    'construction.desc.minimalist': 'Sinpletasunaren elegantzia. Espazio aratuak eta funtzionalak.',
    'construction.type.traditional': 'Obra Tradizionala',
    'construction.desc.traditional': 'Eraikuntza sendoa eta iraunkorra metodo frogatuekin eta material nobleekin.',
    'construction.type.contemporary': 'Etxe Garaikidea',
    'construction.desc.contemporary': 'Gaurko diseinu eguneratua joera eta beharretara egokitzen dena.',
    'construction.type.country': 'Landa Etxea',
    'construction.desc.country': 'Naturan integratua, sendoa eta atsegingarria landa bizitzarako.',
    'construction.type.farmhouse': 'Baserri Berritua',
    'construction.desc.farmhouse': 'Antzinako baserrien historia berreskuratzen dugu zaharberritze teknika errespetuzkoekin.',
    'construction.label': 'Eraikuntza',
    'construction.title': 'Familia Bakarreko Etxeak',
    'construction.subtitle': 'Zure etxea eraikitzen dugu oinarrietatik azken xehetasuneraino.',
    'construction.form.title': 'Eskatu zure aurrekontu pertsonalizatua',
    'construction.form.subtitle': 'Kontatu zure proiektua eta 24 ordutan baino gutxiagoan jarriko gara zurekin harremanetan.',
    'construction.why': 'Zergatik aukeratu gu?',
    'construction.why.desc': '1100 proiektu baino gehiago amaituta, gure esperientziak berak hitz egiten du.',
    'renovation.type.integral': 'Etxebizitzaren Erreforma Integrala',
    'renovation.desc.integral': 'Zure etxearen eraldaketa osoa. Instalazio eta akaberen berritze osoa.',
    'renovation.type.commercial': 'Lokal Komertziala',
    'renovation.desc.commercial': 'Negozioetarako erreforma integralak. Diseinu estrategikoa zure marka indartzeko.',
    'renovation.type.office': 'Bulego Korporatiboak',
    'renovation.desc.office': 'Lan espazio moderno eta eraginkorrak. Bulegoen berritze osoa.',
    'renovation.type.business': 'Negozioak eta Ostalaritza',
    'renovation.desc.business': 'Jatetxe, hotel eta azalera handietarako erreforma handiak.',
    'renovation.label': 'Erreformak',
    'renovation.title': 'Erreforma Integralak',
    'renovation.subtitle': 'Proiektu handietan espezialistak. Erreforma integralak exijentzia gorenarekin.',
    'renovation.h1.title': 'Talde Espezializatua',
    'renovation.h1.desc': 'Profesional kualifikatuen talde zabala diziplina guztietan.',
    'renovation.h2.title': 'Materialetan Adituak',
    'renovation.h2.desc': 'Merkatuko material onenen ezagutza sakona.',
    'renovation.h3.title': 'Eraginkortasun Bermatua',
    'renovation.h3.desc': 'Epeak betetzen ditugu kalitatea arriskuan jarri gabe.',
    'renovation.h4.title': 'Prezio Lehiakorrak',
    'renovation.h4.desc': 'Aurrekontu gardenak eta doituak. Kalitate-prezio harreman onena.',
    'renovation.form.title': 'Eskatu zure erreforma aurrekontua',
    'renovation.form.subtitle': 'Deskribatu zure proiektua eta proposamen zehatza jasoko duzu.',
    'form.name': 'Izen osoa',
    'form.name.placeholder': 'Zure izena',
    'form.email': 'Emaila',
    'form.email.placeholder': 'zu@email.com',
    'form.phone': 'Telefonoa',
    'form.phone.placeholder': '+34 600 000 000',
    'form.location': 'Proiektuaren kokapena',
    'form.location.placeholder': 'Bartzelonako udalerria',
    'form.property.location': 'Jabetzaren kokapena',
    'form.meters': 'Metro karratuak',
    'form.meters.placeholder': '150 m²',
    'form.project.type': 'Proiektu mota',
    'form.project.select': 'Hautatu...',
    'form.timeline': 'Nahi den epea',
    'form.timeline.asap': 'Ahalik eta lasterren',
    'form.timeline.3-6': '3-6 hilabete',
    'form.timeline.6-12': '6-12 hilabete',
    'form.timeline.12+': '12 hilabete baino gehiago',
    'form.renovation.type': 'Erreforma mota',
    'form.budget': 'Gutxi gorabeherako aurrekontua',
    'form.budget.less10k': '10.000€ baino gutxiago',
    'form.budget.10k-25k': '10.000€ - 25.000€',
    'form.budget.25k-50k': '25.000€ - 50.000€',
    'form.budget.50k-100k': '50.000€ - 100.000€',
    'form.budget.100k+': '100.000€ baino gehiago',
    'form.space.desc': 'Espazioaren deskribapena',
    'form.space.placeholder': 'Adib: 80m²-ko pisua, 200m²-ko lokala...',
    'form.details': 'Xehetasun gehigarriak',
    'form.details.placeholder': 'Kontatu gehiago zure proiektu idealari buruz...',
    'form.needs': 'Behar espezifikoak',
    'form.needs.placeholder': 'Deskribatu zer aldaketa behar dituzun...',
    'form.submit': 'Aurrekontua eskatu',
    'form.sending': 'Bidaltzen...',
    'form.success.title': 'Eskaera bidalia!',
    'form.success.message': '24 ordutan baino gutxiagoan jarriko gara zurekin harremanetan.',
    'form.required': '*',
    'form.privacy': 'Formulario hau bidaltzean, gure pribatutasun politika onartzen duzu.',
    'reviews.label': 'Testigantzak',
    'reviews.title1': 'Gure bezeroek',
    'reviews.title2': 'esaten dutena',
    'reviews.subtitle': 'Gure bezeroen gogobetetasuna da gure aurkezpen-gutun onena. 1100 proiektu baino gehiago amaituta gure ibilbidea bermatzen dute.',
    'reviews.tag.construction': 'Eraikuntza',
    'reviews.tag.renovation': 'Erreforma',
    'reviews.stat1': 'Proiektu amaituta',
    'reviews.stat2': 'Urte esperientzia',
    'reviews.stat3': 'Bezero pozik',
    'reviews.stat4': 'Profesionalak',
    'contact.construction': 'Eraikuntza',
    'contact.construction.desc': 'Familia bakarreko etxeen proiektuak, diseinua eta eraikuntza hutsetik.',
    'contact.renovations': 'Erreformak',
    'contact.renovations.desc': 'Erreforma integral osoak etxebizitza eta merkataritza espazioetarako.',
    'contact.call.question': 'Nahiago duzu zuzenean hitz egin?',
    'contact.cta': 'Aurrekontua eskatu',
    'contact.rights': 'Eskubide guztiak erreserbatuta.',
    'contact.privacy': 'Pribatutasun politika',
    'contact.legal': 'Lege oharra',
    'contact.brand.desc': 'Familia bakarreko etxeen eraikuntzan eta erreforma integraletan espezialistak. Konfiantza, eraginkortasuna eta estrategia proiektu guztietan.',
    'contact.barcelona': 'Zerbitzuak Bartzelonako probintzia osoan',
    'contact.tagline': 'Zure etxea, gure pasioa',
    'page.construction.hero.title1': 'Eraikuntza',
    'page.construction.hero.title2': 'Familia Bakarreko Etxeak',
    'page.construction.hero.subtitle': 'Zure ametseko etxea errealitate bihurtzen dugu. Diseinutik giltzen entregaraino.',
    'page.construction.process.label': 'Gure Prozesua',
    'page.construction.process.title': 'Ideatik errealitateraino',
    'page.construction.process.step1.title': 'Hasierako Kontsulta',
    'page.construction.process.step1.desc': 'Zure ideiak, beharrak eta aurrekontua entzuten ditugu.',
    'page.construction.process.step2.title': 'Diseinua eta Proiektua',
    'page.construction.process.step2.desc': 'Zure beharretara egokitutako diseinu arkitektonikoa garatzen dugu.',
    'page.construction.process.step3.title': 'Baimenak eta Lizentziak',
    'page.construction.process.step3.desc': 'Beharrezko dokumentazio guztia kudeatzen dugu.',
    'page.construction.process.step4.title': 'Eraikuntza',
    'page.construction.process.step4.desc': 'Obra egiten dugu material eta profesional onenekin.',
    'page.construction.process.step5.title': 'Entrega',
    'page.construction.process.step5.desc': 'Zure etxe berriaren giltzak ematen dizkizugu.',
    'page.renovation.hero.title1': 'Erreformak',
    'page.renovation.hero.title2': 'Integralak',
    'page.renovation.hero.subtitle': 'Zure espazioa erabat eraldatzen dugu. Txoko guztiak berritzen dituzten erreforma integralak.',
    'page.renovation.process.label': 'Gure Prozesua',
    'page.renovation.process.title': 'Erreforma konplikaziorik gabe',
    'page.renovation.process.step1.title': 'Bisita eta Ebaluazioa',
    'page.renovation.process.step1.desc': 'Zure jabetza bisitatzen dugu egungo egoera ebaluatzeko.',
    'page.renovation.process.step2.title': 'Proposamena eta Aurrekontua',
    'page.renovation.process.step2.desc': 'Proposamen zehatza egiten dugu aurrekontu itxiarekin.',
    'page.renovation.process.step3.title': 'Planifikazioa',
    'page.renovation.process.step3.desc': 'Lan egutegia antolatzen dugu.',
    'page.renovation.process.step4.title': 'Exekuzioa',
    'page.renovation.process.step4.desc': 'Erreforma egiten dugu etengabeko gainbegiratzearekin.',
    'page.renovation.process.step5.title': 'Azken Entrega',
    'page.renovation.process.step5.desc': 'Azken berrikuspen bateratua eta entrega.',
    'page.renovation.why.label': 'Zergatik aukeratu gu',
    'page.renovation.why.title': 'Konfiantzazko erreforma integralak',
    'page.renovation.why.item1': 'Aurrekontu itxia sorpresarik gabe',
    'page.renovation.why.item2': 'Gremio guztien koordinazioa',
    'page.renovation.why.item3': 'Lehen kalitateako materialak',
    'page.renovation.why.item4': 'Obraren eguneko garbiketa',
    'page.renovation.why.item5': '2 urteko bermea akaberetan',
    'page.renovation.why.item6': 'Etengabeko komunikazioa'
  },
  gl: {
    'nav.home': 'Inicio',
    'nav.construction': 'Construción',
    'nav.renovations': 'Reformas',
    'nav.call': 'Chamar agora',
    'values.trust': 'Confianza',
    'values.efficiency': 'Eficiencia',
    'values.strategy': 'Estratexia',
    'values.trust.desc': 'Máis dunha década construíndo relacións sólidas cos nosos clientes',
    'values.efficiency.desc': 'Optimizamos cada proceso para cumprir prazos sen comprometer a calidade',
    'values.strategy.desc': 'Planificación meticulosa desde o primeiro día ata a entrega final',
    'hero.badge': 'Servizos en toda a provincia de Barcelona',
    'hero.title1': 'Construímos',
    'hero.title2': 'o teu futuro fogar',
    'hero.subtitle': 'Especialistas en construción de casas unifamiliares e reformas integrais. Máis dunha década de experiencia transformando espazos con profesionalidade e compromiso.',
    'hero.cta.construction': 'Construción de Casas',
    'hero.cta.renovations': 'Reformas Integrais',
    'hero.trust': 'O teu proxecto nas mellores mans',
    'services.label': 'Os Nosos Servizos',
    'services.title1': 'Dúas especialidades,',
    'services.title2': 'un mesmo compromiso',
    'services.subtitle': 'En Habitalum especializámonos no que mellor sabemos facer: construír fogares e transformar espazos coa máxima calidade.',
    'services.construction.title': 'Construción de Casas Unifamiliares',
    'services.construction.desc': 'Deseñamos e construímos o teu fogar desde cero. Casas modernas, rústicas, mediterráneas ou tradicionais.',
    'services.construction.f1': 'Deseño personalizado',
    'services.construction.f2': 'Materiais premium',
    'services.construction.f3': 'Garantía de calidade',
    'services.construction.f4': 'Prazos garantidos',
    'services.renovations.title': 'Reformas Integrais',
    'services.renovations.desc': 'Transformamos espazos cun equipo altamente especializado.',
    'services.renovations.f1': 'Equipo especializado',
    'services.renovations.f2': 'Expertos en materiais',
    'services.renovations.f3': 'Prezos competitivos',
    'services.renovations.f4': 'Proxectos chave en man',
    'services.more': 'Ver máis información',
    'services.experience': 'Anos de experiencia avalan o noso traballo',
    'construction.type.modern': 'Casa Moderna',
    'construction.desc.modern': 'Liñas limpas, grandes fiestras e espazos abertos que maximizan a luz natural.',
    'construction.type.rustic': 'Casa Rústica',
    'construction.desc.rustic': 'O encanto do tradicional co confort moderno. Pedra, madeira e calidez.',
    'construction.type.mediterranean': 'Estilo Mediterráneo',
    'construction.desc.mediterranean': 'Brancos luminosos, arcos e conexión co exterior. A esencia da nosa terra.',
    'construction.type.minimalist': 'Deseño Minimalista',
    'construction.desc.minimalist': 'A elegancia da simplicidade. Espazos depurados e funcionais.',
    'construction.type.traditional': 'Obra Tradicional',
    'construction.desc.traditional': 'Construción sólida e duradeira con métodos probados e materiais nobres.',
    'construction.type.contemporary': 'Casa Contemporánea',
    'construction.desc.contemporary': 'Deseño actual que se adapta ás tendencias e necesidades de hoxe.',
    'construction.type.country': 'Casa de Campo',
    'construction.desc.country': 'Integrada na natureza, robusta e acolledora para a vida rural.',
    'construction.type.farmhouse': 'Masía Renovada',
    'construction.desc.farmhouse': 'Recuperamos a historia de antigas masías con técnicas de restauración respectuosas.',
    'construction.label': 'Construción',
    'construction.title': 'Casas Unifamiliares',
    'construction.subtitle': 'Construímos o teu fogar desde os cimentos ata o último detalle.',
    'construction.form.title': 'Solicita o teu orzamento personalizado',
    'construction.form.subtitle': 'Cóntanos sobre o teu proxecto e contactarémoste en menos de 24 horas.',
    'construction.why': 'Por que escollernos?',
    'construction.why.desc': 'Con máis de 1100 proxectos completados, a nosa experiencia fala por si soa.',
    'renovation.type.integral': 'Reforma Integral de Vivenda',
    'renovation.desc.integral': 'Transformación total do teu fogar. Renovación completa de instalacións e acabados.',
    'renovation.type.commercial': 'Local Comercial',
    'renovation.desc.commercial': 'Reformas integrais para negocios. Deseño estratéxico para potenciar a túa marca.',
    'renovation.type.office': 'Oficinas Corporativas',
    'renovation.desc.office': 'Espazos de traballo modernos e eficientes. Renovación completa de oficinas.',
    'renovation.type.business': 'Negocios e Hostalería',
    'renovation.desc.business': 'Reformas de gran envergadura para restaurantes, hoteis e grandes superficies.',
    'renovation.label': 'Reformas',
    'renovation.title': 'Reformas Integrais',
    'renovation.subtitle': 'Especialistas en grandes proxectos. Reformas integrais coa máxima esixencia.',
    'renovation.h1.title': 'Equipo Especializado',
    'renovation.h1.desc': 'Persoal amplo de profesionais cualificados en todas as disciplinas.',
    'renovation.h2.title': 'Expertos en Materiais',
    'renovation.h2.desc': 'Coñecemento profundo dos mellores materiais do mercado.',
    'renovation.h3.title': 'Eficiencia Garantida',
    'renovation.h3.desc': 'Cumprimos prazos sen comprometer a calidade.',
    'renovation.h4.title': 'Prezos Competitivos',
    'renovation.h4.desc': 'Orzamentos transparentes e axustados. A mellor relación calidade-prezo.',
    'renovation.form.title': 'Solicita o teu orzamento de reforma',
    'renovation.form.subtitle': 'Describe o teu proxecto e recibirás unha proposta detallada.',
    'form.name': 'Nome completo',
    'form.name.placeholder': 'O teu nome',
    'form.email': 'Email',
    'form.email.placeholder': 'ti@email.com',
    'form.phone': 'Teléfono',
    'form.phone.placeholder': '+34 600 000 000',
    'form.location': 'Ubicación do proxecto',
    'form.location.placeholder': 'Municipio en Barcelona',
    'form.property.location': 'Ubicación da propiedade',
    'form.meters': 'Metros cadrados',
    'form.meters.placeholder': '150 m²',
    'form.project.type': 'Tipo de proxecto',
    'form.project.select': 'Seleccionar...',
    'form.timeline': 'Prazo desexado',
    'form.timeline.asap': 'O antes posible',
    'form.timeline.3-6': '3-6 meses',
    'form.timeline.6-12': '6-12 meses',
    'form.timeline.12+': 'Máis de 12 meses',
    'form.renovation.type': 'Tipo de reforma',
    'form.budget': 'Orzamento aproximado',
    'form.budget.less10k': 'Menos de 10.000€',
    'form.budget.10k-25k': '10.000€ - 25.000€',
    'form.budget.25k-50k': '25.000€ - 50.000€',
    'form.budget.50k-100k': '50.000€ - 100.000€',
    'form.budget.100k+': 'Máis de 100.000€',
    'form.space.desc': 'Descrición do espazo',
    'form.space.placeholder': 'Ex: Piso de 80m², Local de 200m²...',
    'form.details': 'Detalles adicionais',
    'form.details.placeholder': 'Cóntanos máis sobre o teu proxecto ideal...',
    'form.needs': 'Necesidades específicas',
    'form.needs.placeholder': 'Describe que cambios necesitas...',
    'form.submit': 'Solicitar orzamento',
    'form.sending': 'Enviando...',
    'form.success.title': 'Solicitude enviada!',
    'form.success.message': 'Poñerémonos en contacto contigo en menos de 24 horas.',
    'form.required': '*',
    'form.privacy': 'Ao enviar este formulario, aceptas a nosa política de privacidade.',
    'reviews.label': 'Testemuños',
    'reviews.title1': 'O que din',
    'reviews.title2': 'os nosos clientes',
    'reviews.subtitle': 'A satisfacción dos nosos clientes é a nosa mellor carta de presentación. Máis de 1100 proxectos completados avalan a nosa traxectoria.',
    'reviews.tag.construction': 'Construción',
    'reviews.tag.renovation': 'Reforma',
    'reviews.stat1': 'Proxectos completados',
    'reviews.stat2': 'Anos de experiencia',
    'reviews.stat3': 'Clientes satisfeitos',
    'reviews.stat4': 'Profesionais',
    'contact.construction': 'Construción',
    'contact.construction.desc': 'Proxectos de casas unifamiliares, deseño e construción desde cero.',
    'contact.renovations': 'Reformas',
    'contact.renovations.desc': 'Reformas integrais completas para vivendas e espazos comerciais.',
    'contact.call.question': 'Prefires falar directamente?',
    'contact.cta': 'Solicitar orzamento',
    'contact.rights': 'Todos os dereitos reservados.',
    'contact.privacy': 'Política de privacidade',
    'contact.legal': 'Aviso legal',
    'contact.brand.desc': 'Especialistas en construción de casas unifamiliares e reformas integrais. Confianza, eficiencia e estratexia en cada proxecto.',
    'contact.barcelona': 'Servizos en toda a provincia de Barcelona',
    'contact.tagline': 'O teu fogar, a nosa paixón',
    'page.construction.hero.title1': 'Construción de',
    'page.construction.hero.title2': 'Casas Unifamiliares',
    'page.construction.hero.subtitle': 'Facemos realidade o fogar dos teus soños. Desde o deseño ata a entrega de chaves.',
    'page.construction.process.label': 'O Noso Proceso',
    'page.construction.process.title': 'Da idea á realidade',
    'page.construction.process.step1.title': 'Consulta Inicial',
    'page.construction.process.step1.desc': 'Escoitamos as túas ideas, necesidades e orzamento.',
    'page.construction.process.step2.title': 'Deseño e Proxecto',
    'page.construction.process.step2.desc': 'Desenvolvemos o deseño arquitectónico adaptado ás túas necesidades.',
    'page.construction.process.step3.title': 'Permisos e Licenzas',
    'page.construction.process.step3.desc': 'Xestionamos toda a documentación necesaria.',
    'page.construction.process.step4.title': 'Construción',
    'page.construction.process.step4.desc': 'Executamos a obra cos mellores materiais e profesionais.',
    'page.construction.process.step5.title': 'Entrega',
    'page.construction.process.step5.desc': 'Entregámosche as chaves do teu novo fogar.',
    'page.renovation.hero.title1': 'Reformas',
    'page.renovation.hero.title2': 'Integrais',
    'page.renovation.hero.subtitle': 'Transformamos o teu espazo por completo. Reformas integrais que renovan cada recuncho.',
    'page.renovation.process.label': 'O Noso Proceso',
    'page.renovation.process.title': 'Reforma sen complicacións',
    'page.renovation.process.step1.title': 'Visita e Avaliación',
    'page.renovation.process.step1.desc': 'Visitamos a túa propiedade para avaliar o estado actual.',
    'page.renovation.process.step2.title': 'Proposta e Orzamento',
    'page.renovation.process.step2.desc': 'Elaboramos unha proposta detallada con orzamento pechado.',
    'page.renovation.process.step3.title': 'Planificación',
    'page.renovation.process.step3.desc': 'Organizamos o calendario de traballo.',
    'page.renovation.process.step4.title': 'Execución',
    'page.renovation.process.step4.desc': 'Realizamos a reforma con supervisión constante.',
    'page.renovation.process.step5.title': 'Entrega Final',
    'page.renovation.process.step5.desc': 'Revisión final conxunta e entrega.',
    'page.renovation.why.label': 'Por que escollernos',
    'page.renovation.why.title': 'Reformas integrais de confianza',
    'page.renovation.why.item1': 'Orzamento pechado sen sorpresas',
    'page.renovation.why.item2': 'Coordinación de todos os gremios',
    'page.renovation.why.item3': 'Materiais de primeira calidade',
    'page.renovation.why.item4': 'Limpeza diaria da obra',
    'page.renovation.why.item5': 'Garantía de 2 anos en acabados',
    'page.renovation.why.item6': 'Comunicación constante'
  },
  en: {
    'nav.home': 'Home',
    'nav.construction': 'Construction',
    'nav.renovations': 'Renovations',
    'nav.call': 'Call now',
    'values.trust': 'Trust',
    'values.efficiency': 'Efficiency',
    'values.strategy': 'Strategy',
    'values.trust.desc': 'Over a decade building solid relationships with our clients',
    'values.efficiency.desc': 'We optimize every process to meet deadlines without compromising quality',
    'values.strategy.desc': 'Meticulous planning from day one to final delivery',
    'hero.badge': 'Services throughout Barcelona province',
    'hero.title1': 'We build',
    'hero.title2': 'your future home',
    'hero.subtitle': 'Specialists in single-family home construction and integral renovations. Over a decade of experience transforming spaces with professionalism, quality and absolute commitment.',
    'hero.cta.construction': 'Home Construction',
    'hero.cta.renovations': 'Integral Renovations',
    'hero.trust': 'Your project in the best hands',
    'services.label': 'Our Services',
    'services.title1': 'Two specialties,',
    'services.title2': 'one commitment',
    'services.subtitle': 'At Habitalum we specialize in what we do best: building homes and transforming spaces with the highest quality.',
    'services.construction.title': 'Single-Family Home Construction',
    'services.construction.desc': 'We design and build your home from scratch. Modern, rustic, Mediterranean or traditional houses.',
    'services.construction.f1': 'Custom design',
    'services.construction.f2': 'Premium materials',
    'services.construction.f3': 'Quality guarantee',
    'services.construction.f4': 'Guaranteed deadlines',
    'services.renovations.title': 'Integral Renovations',
    'services.renovations.desc': 'We transform spaces with a highly specialized team.',
    'services.renovations.f1': 'Specialized team',
    'services.renovations.f2': 'Material experts',
    'services.renovations.f3': 'Competitive prices',
    'services.renovations.f4': 'Turnkey projects',
    'services.more': 'See more information',
    'services.experience': 'Years of experience back our work',
    'construction.type.modern': 'Modern Home',
    'construction.desc.modern': 'Clean lines, large windows and open spaces that maximize natural light.',
    'construction.type.rustic': 'Rustic Home',
    'construction.desc.rustic': 'The charm of traditional with modern comfort. Stone, wood and warmth.',
    'construction.type.mediterranean': 'Mediterranean Style',
    'construction.desc.mediterranean': 'Bright whites, arches and connection with the outdoors. The essence of our land.',
    'construction.type.minimalist': 'Minimalist Design',
    'construction.desc.minimalist': 'The elegance of simplicity. Clean, functional spaces.',
    'construction.type.traditional': 'Traditional Construction',
    'construction.desc.traditional': 'Solid, durable construction with proven methods and noble materials.',
    'construction.type.contemporary': 'Contemporary Home',
    'construction.desc.contemporary': "Current design that adapts to today's trends and needs.",
    'construction.type.country': 'Country House',
    'construction.desc.country': 'Integrated into nature, robust and cozy for rural living.',
    'construction.type.farmhouse': 'Renovated Farmhouse',
    'construction.desc.farmhouse': 'We recover the history of old farmhouses with respectful restoration techniques.',
    'construction.label': 'Construction',
    'construction.title': 'Single-Family Homes',
    'construction.subtitle': 'We build your home from the foundation to the last detail.',
    'construction.form.title': 'Request your personalized quote',
    'construction.form.subtitle': "Tell us about your project and we'll contact you within 24 hours.",
    'construction.why': 'Why choose us?',
    'construction.why.desc': 'With over 1100 completed projects, our experience speaks for itself.',
    'renovation.type.integral': 'Integral Home Renovation',
    'renovation.desc.integral': 'Total transformation of your home. Complete renovation of installations and finishes.',
    'renovation.type.commercial': 'Commercial Premises',
    'renovation.desc.commercial': 'Integral renovations for businesses. Strategic design to boost your brand.',
    'renovation.type.office': 'Corporate Offices',
    'renovation.desc.office': 'Modern and efficient workspaces. Complete office renovation.',
    'renovation.type.business': 'Business & Hospitality',
    'renovation.desc.business': 'Large-scale renovations for restaurants, hotels and large surfaces.',
    'renovation.label': 'Renovations',
    'renovation.title': 'Integral Renovations',
    'renovation.subtitle': 'Specialists in large projects. Integral renovations with the highest standards.',
    'renovation.h1.title': 'Specialized Team',
    'renovation.h1.desc': 'Large staff of qualified professionals in all disciplines.',
    'renovation.h2.title': 'Material Experts',
    'renovation.h2.desc': 'Deep knowledge of the best materials on the market.',
    'renovation.h3.title': 'Guaranteed Efficiency',
    'renovation.h3.desc': 'We meet deadlines without compromising quality.',
    'renovation.h4.title': 'Competitive Prices',
    'renovation.h4.desc': 'Transparent and adjusted budgets. The best value for money.',
    'renovation.form.title': 'Request your renovation quote',
    'renovation.form.subtitle': "Describe your project and you'll receive a detailed proposal.",
    'form.name': 'Full name',
    'form.name.placeholder': 'Your name',
    'form.email': 'Email',
    'form.email.placeholder': 'you@email.com',
    'form.phone': 'Phone',
    'form.phone.placeholder': '+34 600 000 000',
    'form.location': 'Project location',
    'form.location.placeholder': 'Municipality in Barcelona',
    'form.property.location': 'Property location',
    'form.meters': 'Square meters',
    'form.meters.placeholder': '150 m²',
    'form.project.type': 'Project type',
    'form.project.select': 'Select...',
    'form.timeline': 'Desired timeline',
    'form.timeline.asap': 'As soon as possible',
    'form.timeline.3-6': '3-6 months',
    'form.timeline.6-12': '6-12 months',
    'form.timeline.12+': 'More than 12 months',
    'form.renovation.type': 'Renovation type',
    'form.budget': 'Approximate budget',
    'form.budget.less10k': 'Less than €10,000',
    'form.budget.10k-25k': '€10,000 - €25,000',
    'form.budget.25k-50k': '€25,000 - €50,000',
    'form.budget.50k-100k': '€50,000 - €100,000',
    'form.budget.100k+': 'More than €100,000',
    'form.space.desc': 'Space description',
    'form.space.placeholder': 'E.g.: 80m² apartment, 200m² premises...',
    'form.details': 'Additional details',
    'form.details.placeholder': 'Tell us more about your ideal project...',
    'form.needs': 'Specific needs',
    'form.needs.placeholder': 'Describe what changes you need...',
    'form.submit': 'Request quote',
    'form.sending': 'Sending...',
    'form.success.title': 'Request sent!',
    'form.success.message': "We'll contact you within 24 hours.",
    'form.required': '*',
    'form.privacy': 'By submitting this form, you accept our privacy policy.',
    'reviews.label': 'Testimonials',
    'reviews.title1': 'What our',
    'reviews.title2': 'clients say',
    'reviews.subtitle': "Our clients' satisfaction is our best introduction. Over 1100 completed projects back our track record.",
    'reviews.tag.construction': 'Construction',
    'reviews.tag.renovation': 'Renovation',
    'reviews.stat1': 'Completed projects',
    'reviews.stat2': 'Years of experience',
    'reviews.stat3': 'Satisfied clients',
    'reviews.stat4': 'Professionals',
    'contact.construction': 'Construction',
    'contact.construction.desc': 'Single-family home projects, design and construction from scratch.',
    'contact.renovations': 'Renovations',
    'contact.renovations.desc': 'Complete integral renovations for homes and commercial spaces.',
    'contact.call.question': 'Prefer to talk directly?',
    'contact.cta': 'Request quote',
    'contact.rights': 'All rights reserved.',
    'contact.privacy': 'Privacy policy',
    'contact.legal': 'Legal notice',
    'contact.brand.desc': 'Specialists in single-family home construction and integral renovations. Trust, efficiency and strategy in every project.',
    'contact.barcelona': 'Services throughout Barcelona province',
    'contact.tagline': 'Your home, our passion',
    'page.construction.hero.title1': 'Construction of',
    'page.construction.hero.title2': 'Single-Family Homes',
    'page.construction.hero.subtitle': 'We make your dream home a reality. From design to key delivery.',
    'page.construction.process.label': 'Our Process',
    'page.construction.process.title': 'From idea to reality',
    'page.construction.process.step1.title': 'Initial Consultation',
    'page.construction.process.step1.desc': 'We listen to your ideas, needs and budget.',
    'page.construction.process.step2.title': 'Design and Project',
    'page.construction.process.step2.desc': 'We develop the architectural design adapted to your needs.',
    'page.construction.process.step3.title': 'Permits and Licenses',
    'page.construction.process.step3.desc': 'We manage all necessary documentation.',
    'page.construction.process.step4.title': 'Construction',
    'page.construction.process.step4.desc': 'We execute the work with the best materials and professionals.',
    'page.construction.process.step5.title': 'Delivery',
    'page.construction.process.step5.desc': 'We hand you the keys to your new home.',
    'page.renovation.hero.title1': 'Integral',
    'page.renovation.hero.title2': 'Renovations',
    'page.renovation.hero.subtitle': 'We transform your space completely. Integral renovations that renew every corner.',
    'page.renovation.process.label': 'Our Process',
    'page.renovation.process.title': 'Hassle-free renovation',
    'page.renovation.process.step1.title': 'Visit and Evaluation',
    'page.renovation.process.step1.desc': 'We visit your property to evaluate the current state.',
    'page.renovation.process.step2.title': 'Proposal and Budget',
    'page.renovation.process.step2.desc': 'We prepare a detailed proposal with a closed budget.',
    'page.renovation.process.step3.title': 'Planning',
    'page.renovation.process.step3.desc': 'We organize the work schedule.',
    'page.renovation.process.step4.title': 'Execution',
    'page.renovation.process.step4.desc': 'We carry out the renovation with constant supervision.',
    'page.renovation.process.step5.title': 'Final Delivery',
    'page.renovation.process.step5.desc': 'Joint final review and delivery.',
    'page.renovation.why.label': 'Why choose us',
    'page.renovation.why.title': 'Trusted integral renovations',
    'page.renovation.why.item1': 'Closed budget with no surprises',
    'page.renovation.why.item2': 'Coordination of all trades',
    'page.renovation.why.item3': 'First quality materials',
    'page.renovation.why.item4': 'Daily cleaning of the site',
    'page.renovation.why.item5': '2-year warranty on finishes',
    'page.renovation.why.item6': 'Constant communication'
  }
};
export function LanguageProvider({
  children
}: {
  children: ReactNode;
}) {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('habitalum-language');
      return saved as Language || 'es';
    }
    return 'es';
  });
  useEffect(() => {
    localStorage.setItem('habitalum-language', language);
    document.documentElement.lang = language;
  }, [language]);
  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };
  const t = (key: string): string => {
    return translations[language][key] || translations['es'][key] || key;
  };
  return <LanguageContext.Provider value={{
    language,
    setLanguage,
    t
  }} data-id="element-337">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}